import type { CSSProperties } from 'react';
import './AvatarFrameDecoration.css';

export type FrameDecoration = 'plain' | 'stars' | 'falling';
export type FallingItem = 'snow' | 'leaves' | 'flowers' | 'gold' | 'hearts';
export interface FrameEffects {
  starColors: [string, string, string];
  starGlow: [string, string];
  falling: FallingItem;
  fallingColor?: string;
  extraStar?: boolean;
}

function FallingShape({ kind }: { kind: FallingItem }) {
  if (kind === 'hearts')
    return (
      <svg viewBox="0 0 24 24">
        <path
          d="M12 21C9 18 2 13 2 7.5C2 2 9 1 12 6C15 1 22 2 22 7.5C22 13 15 18 12 21Z"
          fill="#dc4b50"
          stroke="#e8807b"
          strokeWidth=".7"
        />
        <path
          d="M5 8c0-2 2-3 3-2"
          fill="none"
          stroke="#efaaa0"
          strokeWidth="1"
          strokeLinecap="round"
        />
      </svg>
    );
  if (kind === 'flowers')
    return (
      <svg viewBox="0 0 24 24" fill="#fff5dd">
        {[0, 72, 144, 216, 288].map((angle) => (
          <ellipse
            key={angle}
            cx="12"
            cy="6.5"
            rx="3.2"
            ry="5"
            transform={`rotate(${angle} 12 12)`}
          />
        ))}
        <circle cx="12" cy="12" r="3" fill="#e9b84a" />
      </svg>
    );
  if (kind === 'gold')
    return (
      <svg viewBox="0 0 20 30">
        <path d="M10 2 12.2 11.8 20 15 12.2 18.2 10 28 7.8 18.2 0 15 7.8 11.8Z" fill="#f2c76b" />
        <path d="M10 8 11.3 13 15 15 11.3 17 10 22 8.7 17 5 15 8.7 13Z" fill="#fff1c1" />
      </svg>
    );
  if (kind === 'leaves')
    return (
      <svg viewBox="0 0 24 30">
        <path
          d="M12 1 15 7 17 5 16 12 21 9 20 13 24 14 20 18 22 20 15 22 12 24 9 22 2 20 4 18 0 14 4 13 3 9 8 12 7 5 9 7Z"
          fill="currentColor"
          stroke="#a44b24"
          strokeWidth=".55"
          strokeLinejoin="round"
        />
        <path
          d="M12 5v23M12 17 6 13M12 20l-6-1M12 17l6-4M12 20l6-1M12 12l-2-3m2 3 2-3"
          stroke="#ffd287"
          strokeOpacity=".65"
          strokeWidth=".7"
          strokeLinecap="round"
        />
      </svg>
    );
  return (
    <svg
      viewBox="0 0 40 40"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    >
      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <path
          key={angle}
          transform={`rotate(${angle} 20 20)`}
          d="M20 20V3M20 10l-5-4m5 4 5-4M20 15l-4-3m4 3 4-3"
        />
      ))}
    </svg>
  );
}

const stars = [
  [16, 34, 6],
  [80, 26, 5],
  [84, 72, 6.5],
  [30, 87, 4],
];
const falling = [
  [14, 25, 10, -2],
  [84, 40, 12, -6],
  [73, 80, 11, -8],
  [30, 65, 13, -4],
];
const leafColors = ['#e8a33d', '#d97135', '#c65036', '#eac068'];

/** Decorative layers are clipped independently so frame artwork can overflow the avatar. */
export function AvatarFrameDecoration({
  mode,
  effects,
}: {
  mode: FrameDecoration;
  effects: FrameEffects;
}) {
  if (mode === 'plain') return null;
  const style = {
    '--star-center': effects.starColors[0],
    '--star-middle': effects.starColors[1],
    '--star-edge': effects.starColors[2],
    '--star-glow': effects.starGlow[0],
    '--star-halo': effects.starGlow[1],
  } as CSSProperties;
  return (
    <span
      className={`avatar-frame-decoration avatar-frame-decoration--${mode}`}
      style={style}
      aria-hidden="true"
    >
      {mode === 'stars' && (
        <>
          <span className="avatar-frame-decoration__dots" />
          {(effects.extraStar ? [...stars, [25, 72, 5]] : stars).map(([x, y, size], i) => (
            <span
              key={i}
              className="avatar-frame-decoration__star"
              style={
                {
                  left: `${x}%`,
                  top: `${y}%`,
                  width: `${size}%`,
                  height: `${size}%`,
                  '--duration': `${8 + i * 0.5}s`,
                  '--delay': `${-i * 2}s`,
                } as CSSProperties
              }
            >
              <span />
            </span>
          ))}
        </>
      )}
      {mode === 'falling' &&
        falling.map(([x, y, duration, delay], i) => (
          <span
            key={i}
            className={`avatar-frame-decoration__fall avatar-frame-decoration__fall--${effects.falling}`}
            style={
              {
                left: `${x}%`,
                '--rest-y': `${y}%`,
                '--duration': `${duration}s`,
                '--delay': `${delay}s`,
                color:
                  effects.falling === 'leaves' ? leafColors[i] : effects.fallingColor || '#e4f4ff',
              } as CSSProperties
            }
          >
            <FallingShape kind={effects.falling} />
          </span>
        ))}
    </span>
  );
}
